import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorMessage } from '../components/ErrorMessage';
import { RaceSelector } from '../components/RaceSelector';
import { PersonalitySelector } from '../components/PersonalitySelector';
import { RACES, PERSONALITIES, GENDERS, type Race, type Personality, type Gender } from '../constants/game';
import { createProvince } from '../api/province';
import { getRaces, getPersonalities, type RaceConfig, type PersonalityConfig } from '../api/gameConfig';
import { validateProvinceName, validateRulerName } from '../utils/validation';
import { COLORS } from '../constants/theme';

const DEFAULT_WORLD_ID = 'default';

function StepIndicator({ step }: { step: 1 | 2 }) {
  const stepStyle = (n: number): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: step === n ? COLORS.link : step > n ? COLORS.text.tertiary : COLORS.text.muted,
    fontWeight: step === n ? 600 : 400,
    fontSize: 14,
  });
  const pipStyle = (n: number): React.CSSProperties => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    backgroundColor: step === n ? COLORS.link : step > n ? COLORS.border : 'transparent',
    border: `2px solid ${step === n ? COLORS.link : step > n ? COLORS.border : COLORS.text.muted}`,
    color: step === n ? COLORS.bg : COLORS.text.muted,
    flexShrink: 0,
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={stepStyle(1)}>
        <span style={pipStyle(1)}>{step > 1 ? '✓' : '1'}</span>
        Race &amp; Personality
      </div>
      <div style={{ width: 40, height: 1, backgroundColor: COLORS.border }} />
      <div style={stepStyle(2)}>
        <span style={pipStyle(2)}>2</span>
        Province Details
      </div>
    </div>
  );
}

export function CreateProvince() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const { worldId: pathWorldId } = useParams<{ worldId?: string }>();
  const worldId = pathWorldId || DEFAULT_WORLD_ID;

  const [step, setStep] = useState<1 | 2>(1);

  const [provinceName, setProvinceName] = useState('');
  const [rulerName, setRulerName] = useState('');
  const [gender, setGender] = useState<Gender>(GENDERS[0].id);
  const [race, setRace] = useState<Race>(RACES[0].id);
  const [personality, setPersonality] = useState<Personality>(PERSONALITIES[0].id);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [errors, setErrors] = useState<{ provinceName?: string; rulerName?: string }>({});

  const [races, setRaces] = useState<RaceConfig[] | null>(null);
  const [personalities, setPersonalities] = useState<PersonalityConfig[] | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);

  const loadGameConfig = async () => {
    setConfigLoading(true);
    setConfigError(false);
    const [racesResult, personalitiesResult] = await Promise.all([
      getRaces(),
      getPersonalities(),
    ]);
    if (racesResult.error || personalitiesResult.error) {
      setConfigError(true);
      setConfigLoading(false);
      return;
    }
    setRaces(racesResult.data?.races ?? null);
    setPersonalities(personalitiesResult.data?.personalities ?? null);
    setConfigLoading(false);
  };

  useEffect(() => { loadGameConfig(); }, []);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    if (!validateProvinceName(provinceName)) newErrors.provinceName = 'Province name must be 3-25 characters';
    if (!validateRulerName(rulerName)) newErrors.rulerName = 'Ruler name must be 3-25 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;
    setIsLoading(true);

    const { error: createError } = await createProvince(worldId, {
      provinceName,
      rulerName,
      gender,
      race,
      personality,
    });

    if (createError) {
      setError(createError);
      setIsLoading(false);
      return;
    }

    await refreshUser();
    navigate('/province');
  };

  const showCards = !configLoading && !configError && races && personalities;
  const showFallback = !configLoading && (configError || !races || !personalities);

  return (
    <div
      className="min-h-screen flex flex-col items-center py-10 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div style={{ width: '100%', maxWidth: step === 1 ? 1152 : 480 }}>
        {/* Header */}
        <h1
          className="text-center text-3xl font-bold tracking-widest mb-6"
          style={{ color: COLORS.text.primary }}
        >
          CREATE YOUR PROVINCE
        </h1>

        <div style={{ marginBottom: 32 }}>
          <StepIndicator step={step} />
        </div>

        {/* ── Step 1: Race & Personality ── */}
        {step === 1 && (
          <div className="space-y-10">
            {configError && (
              <div style={{
                backgroundColor: COLORS.errorBg,
                border: `1px solid ${COLORS.errorBorder}`,
                borderRadius: 8,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
                <span style={{ color: COLORS.errorText, fontSize: 14 }}>
                  Couldn't load detailed race/personality info.
                </span>
                <button
                  type="button"
                  onClick={loadGameConfig}
                  style={{ color: COLORS.link, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: 14, textDecoration: 'underline', flexShrink: 0 }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Race */}
            <section>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text.tertiary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
                Race
              </div>
              {configLoading && (
                <div style={{ color: COLORS.text.muted, fontSize: 13 }}>Loading…</div>
              )}
              {showCards && races && (
                <RaceSelector races={races} selectedId={race} onSelect={(id) => setRace(id as Race)} />
              )}
              {showFallback && (
                <Select label="" options={RACES} value={race} onChange={(e) => setRace(e.target.value as Race)} />
              )}
            </section>

            {/* Personality */}
            <section>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.text.tertiary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
                Personality
              </div>
              {configLoading && (
                <div style={{ color: COLORS.text.muted, fontSize: 13 }}>Loading…</div>
              )}
              {showCards && personalities && (
                <PersonalitySelector personalities={personalities} selectedId={personality} onSelect={(id) => setPersonality(id as Personality)} />
              )}
              {showFallback && (
                <Select label="" options={PERSONALITIES} value={personality} onChange={(e) => setPersonality(e.target.value as Personality)} />
              )}
            </section>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 24 }}>
              <Button type="button" onClick={() => setStep(2)}>
                Next →
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Province Details ── */}
        {step === 2 && (
          <form
            className="space-y-6 p-8 rounded-lg"
            style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
            onSubmit={handleSubmit}
          >
            <div className="space-y-4">
              <Input
                label="Province Name"
                type="text"
                value={provinceName}
                onChange={(e) => setProvinceName(e.target.value)}
                error={errors.provinceName}
                hint="3-25 characters"
                placeholder="Kingdom of Steel"
                required
              />
              <Input
                label="Ruler Name"
                type="text"
                value={rulerName}
                onChange={(e) => setRulerName(e.target.value)}
                error={errors.rulerName}
                hint="3-25 characters"
                placeholder="Lord Aldric"
                required
              />
              <Select
                label="Gender"
                options={GENDERS}
                value={gender}
                onChange={(e) => setGender(e.target.value as Gender)}
              />
            </div>

            {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 6,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: 'transparent',
                  color: COLORS.text.secondary,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                ← Back
              </button>
              <div style={{ flex: 1 }}>
                <Button type="submit" isLoading={isLoading} className="w-full">
                  Create Province
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
